import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Form, Button, Badge, Alert, Modal } from 'react-bootstrap';
import authService from '../services/authService';

function FeedbackAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({
    status: '',
    priority: '',
    admin_notes: '',
    admin_category: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const checkAuthAndLoad = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load feedback
    try {
      const url = statusFilter !== 'all'
        ? `/api/feedback/admin/list?status=${statusFilter}`
        : '/api/feedback/admin/list';

      const response = await fetch(url, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setFeedback(data.feedback || []);
      } else if (response.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else {
        setError('Failed to load feedback');
      }
    } catch (error) {
      setError('Error loading feedback: ' + error.message);
    }

    setLoading(false);
  };

  const handleEdit = (item) => {
    setSelectedFeedback(item);
    setEditData({
      status: item.status || 'new',
      priority: item.priority || '',
      admin_notes: item.admin_notes || '',
      admin_category: item.admin_category || ''
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      const response = await fetch(`/api/feedback/admin/update/${selectedFeedback.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        setShowEditModal(false);
        checkAuthAndLoad(); // Reload data
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update feedback');
      }
    } catch (err) {
      setError('Error updating feedback: ' + err.message);
    }

    setSaving(false);
  };

  const getRatingStars = (rating) => {
    if (!rating) return <span className="text-muted">-</span>;
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const getStatusBadge = (status) => {
    const variants = {
      'new': 'primary',
      'reviewed': 'info',
      'action_taken': 'success',
      'on_roadmap': 'warning',
      'user_contacted': 'secondary',
      'dismissed': 'dark'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    if (!priority) return null;
    const variants = {
      'critical': 'danger',
      'high': 'warning',
      'medium': 'info',
      'low': 'secondary'
    };
    return <Badge bg={variants[priority] || 'secondary'}>{priority}</Badge>;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card className="shadow">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>💬 Feedback Admin</h2>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/dashboard')}
                >
                  ← Back to Dashboard
                </Button>
              </div>

              {error && <Alert variant="danger">{error}</Alert>}

              <Row className="mb-4">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Filter by Status</Form.Label>
                    <Form.Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="new">New</option>
                      <option value="reviewed">Reviewed</option>
                      <option value="action_taken">Action Taken</option>
                      <option value="on_roadmap">On Roadmap</option>
                      <option value="user_contacted">User Contacted</option>
                      <option value="dismissed">Dismissed</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={8} className="text-end pt-4">
                  <p className="text-muted mb-0">
                    <strong>{feedback.length}</strong> feedback submission{feedback.length !== 1 ? 's' : ''}
                  </p>
                </Col>
              </Row>

              <div className="table-responsive">
                <Table striped hover>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>User</th>
                      <th>Module</th>
                      <th>Ease</th>
                      <th>Quality</th>
                      <th>Feedback</th>
                      <th>📷</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feedback.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="text-center text-muted">
                          No feedback submissions found
                        </td>
                      </tr>
                    ) : (
                      feedback.map((item) => (
                        <tr key={item.id}>
                          <td className="small">
                            {new Date(item.created_at).toLocaleDateString()}
                          </td>
                          <td className="small">{item.user_email}</td>
                          <td className="small">{item.module_name}</td>
                          <td>{getRatingStars(item.ease_rating)}</td>
                          <td>{getRatingStars(item.quality_rating)}</td>
                          <td className="small" style={{ maxWidth: '200px' }}>
                            {item.feedback_text ? (
                              <span title={item.feedback_text}>
                                {item.feedback_text.substring(0, 50)}
                                {item.feedback_text.length > 50 ? '...' : ''}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="text-center">
                            {item.screenshot_path ? (
                              <span title="Has screenshot">📷</span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>{getStatusBadge(item.status)}</td>
                          <td>{getPriorityBadge(item.priority)}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleEdit(item)}
                            >
                              Review
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Review Feedback</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFeedback && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>User:</strong> {selectedFeedback.user_email}</p>
                  <p><strong>Module:</strong> {selectedFeedback.module_name}</p>
                  <p><strong>Date:</strong> {new Date(selectedFeedback.created_at).toLocaleString()}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Ease Rating:</strong> {getRatingStars(selectedFeedback.ease_rating)}</p>
                  <p><strong>Quality Rating:</strong> {getRatingStars(selectedFeedback.quality_rating)}</p>
                  <p><strong>URL:</strong> <a href={selectedFeedback.url} target="_blank" rel="noreferrer">{selectedFeedback.url}</a></p>
                </Col>
              </Row>

              {selectedFeedback.feedback_text && (
                <div className="mb-3">
                  <strong>Feedback:</strong>
                  <div className="p-3 bg-light rounded mt-2">
                    {selectedFeedback.feedback_text}
                  </div>
                </div>
              )}

              {selectedFeedback.screenshot_path && (
                <div className="mb-3">
                  <strong>Screenshot:</strong>
                  <div className="mt-2">
                    <a
                      href={`/static/${selectedFeedback.screenshot_path}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src={`/static/${selectedFeedback.screenshot_path}`}
                        alt="User screenshot"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '400px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      />
                    </a>
                    <p className="small text-muted mt-1">
                      Click image to view full size in new tab
                    </p>
                  </div>
                </div>
              )}

              <hr />

              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={editData.status}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                >
                  <option value="new">New</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="action_taken">Action Taken</option>
                  <option value="on_roadmap">On Roadmap</option>
                  <option value="user_contacted">User Contacted</option>
                  <option value="dismissed">Dismissed</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  value={editData.priority}
                  onChange={(e) => setEditData({ ...editData, priority: e.target.value })}
                >
                  <option value="">-- Select Priority --</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={editData.admin_category}
                  onChange={(e) => setEditData({ ...editData, admin_category: e.target.value })}
                >
                  <option value="">-- Select Category --</option>
                  <option value="bug">Bug</option>
                  <option value="feature_request">Feature Request</option>
                  <option value="ux_issue">UX Issue</option>
                  <option value="content_quality">Content Quality</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Admin Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={editData.admin_notes}
                  onChange={(e) => setEditData({ ...editData, admin_notes: e.target.value })}
                  placeholder="Add internal notes about this feedback..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default FeedbackAdmin;
